import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import { userService } from '@/shared/services/userService';
import usePageTitle from '@/shared/hooks/usePageTitle';
import {
    OPTIONAL_EMAILS,
    AlwaysSentEmails,
    readEmailPreferences,
} from './EmailPreferences';

function EditProfile() {
    usePageTitle('Edit profile');
    const { userProfile, refreshProfile } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        department: '',
        phone: '',
    });
    const [emailPreferences, setEmailPreferences] = useState(
        readEmailPreferences(null)
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (userProfile) {
            setFormData({
                name: userProfile.name || '',
                department: userProfile.department || '',
                phone: userProfile.phone || '',
            });
            setEmailPreferences(readEmailPreferences(userProfile));
        }
    }, [userProfile]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePreference = (e) => {
        const { name, checked } = e.target;
        setEmailPreferences((prev) => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await userService.updateProfile({ ...formData, emailPreferences });
            refreshProfile();
            navigate('/profile');
        } catch (_err) {
            setError('Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="py-4">
            <Row>
                <Col lg={6} className="mx-auto">
                    <Card>
                        <Card.Header>
                            <h1 className="h4 mb-0">Edit Profile</h1>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Full Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        autoComplete="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group
                                    className="mb-3"
                                    controlId="department"
                                >
                                    <Form.Label>Department</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="phone">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="phone"
                                        autoComplete="tel"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <fieldset className="mb-3">
                                    <legend className="h6">Email</legend>
                                    {OPTIONAL_EMAILS.map(
                                        ({ key, label, help }) => (
                                            <Form.Group
                                                key={key}
                                                className="mb-2"
                                                controlId={key}
                                            >
                                                <Form.Check
                                                    type="switch"
                                                    // react-bootstrap styles a
                                                    // switch but leaves it a
                                                    // plain checkbox to
                                                    // assistive technology.
                                                    role="switch"
                                                    name={key}
                                                    checked={
                                                        emailPreferences[key]
                                                    }
                                                    onChange={handlePreference}
                                                    label={label}
                                                    aria-describedby={`${key}-help`}
                                                />
                                                <Form.Text
                                                    id={`${key}-help`}
                                                    muted
                                                >
                                                    {help}
                                                </Form.Text>
                                            </Form.Group>
                                        )
                                    )}
                                    <AlwaysSentEmails headingLevel="h2" />
                                </fieldset>

                                <div className="d-flex gap-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={() => navigate('/profile')}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default EditProfile;
